import type { Url } from 'next/dist/shared/lib/router/router';
import type { LinkProps } from 'next/link';
import type { CSSProperties } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { cn } from '@poseidon/components/cn';
import React from 'react';

import { Button } from '@poseidon/components/Button';
import { Arrows, General } from '@poseidon/components/StrokeIcons';
import { Tooltip } from '@poseidon/components/Tooltip';

import { ButtonLink } from '@/components/Button';
import NotificationLabel from '~/components/NotificationLabel';
import styles from './LeftNav.module.scss';

type RowProps = LinkProps & {
  icon: React.JSX.Element;
  children?: React.ReactNode;
  className?: string;
  minimized?: boolean;
  counterValue?: number;
  subLinks?: RowProps[];
  addLink?: Url;
  addLinkLabel?: string;
  newBadge?: boolean;
};

export const LeftNavRow = ({
  href,
  icon,
  className,
  children,
  minimized,
  counterValue,
  subLinks,
  addLink,
  addLinkLabel,
  newBadge,
  ...props
}: RowProps) => {
  const { pathname } = useRouter();
  const subLinksRef = React.useRef<HTMLDivElement>(null);
  const [wasOpened, setWasOpened] = React.useState(() => {
    return getSublinksUrls(subLinks).includes(pathname);
  });
  const [isOpen, setIsOpen] = React.useState(() => {
    return getSublinksUrls(subLinks).includes(pathname);
  });

  const [wasManuallyOpened, setWasManuallyOpened] = React.useState(false);

  // eslint-disable-next-line @typescript-eslint/no-base-to-string
  const hrefString = href.toString();

  const classList = cn(
    className,
    styles.row,
    styles.hoverable,
    pathname === hrefString ? styles.active : undefined,
    pathname.startsWith(hrefString) ? styles.partialActive : undefined,
  );

  const hasActions = !minimized && ((subLinks?.length ?? 0) > 0 || addLink != null);
  const changeOpen = (newState: boolean) => {
    setIsOpen(newState);
    setWasOpened(true);
    setWasManuallyOpened(true);
  };

  const subLinkStyles = {
    '--height': (subLinksRef.current?.scrollHeight ?? 0) + 'px',
  } as CSSProperties;
  return (
    <div
      style={subLinkStyles}
      onMouseEnter={() => {
        if (minimized) {
          changeOpen(true);
        }
      }}
      onMouseLeave={() => {
        if (minimized) {
          changeOpen(false);
        }
      }}>
      <Tooltip label={minimized ? children : undefined}>
        <div className={cn(styles.linkWrapper, 'group relative w-full')}>
          <Link href={href} className={classList} {...props}>
            <span className="size-6">{icon}</span>
            <span className={cn(styles.rowText, 'whitespace-pre')}>{children}</span>
          </Link>
          {!!counterValue && (
            <NotificationLabel
              className={cn(styles.notification, minimized ? styles.minimizedNotification : undefined)}
              counterValue={counterValue}
            />
          )}
          {hasActions && (
            <div className="absolute right-1 top-1/2 flex -translate-y-1/2 gap-1 opacity-60 transition-opacity group-hover:opacity-100">
              {addLink && (
                <ButtonLink
                  href={addLink}
                  size="sm"
                  text
                  tooltip={addLinkLabel}
                  onClick={() => changeOpen(!isOpen)}
                  icon={<General.PlusCircle />}
                />
              )}
              {(subLinks?.length ?? 0) > 0 && (
                <Button
                  text
                  size="sm"
                  onClick={() => changeOpen(!isOpen)}
                  icon={<Arrows.ChevronDown className={cn('transition-all', { 'rotate-180': isOpen })} />}
                />
              )}
            </div>
          )}
          {newBadge && (
            <div
              className={cn(
                'pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 rounded-md px-1 py-0.5 text-2xs font-semibold uppercase',
                'bg-gradient-to-r from-purple-600 to-zumthor-600 text-white',
                { hidden: minimized },
              )}>
              {' '}
              new{' '}
            </div>
          )}
        </div>
      </Tooltip>
      {(subLinks?.length ?? 0) > 0 && (
        <div
          className={cn(
            styles.subLinksWrapper,
            wasOpened && wasManuallyOpened && (isOpen ? styles.subLinksWrapperOpened : styles.subLinksWrapperClosed),
          )}
          style={isOpen ? { height: 'auto' } : { height: '0px' }}>
          <div
            ref={subLinksRef}
            className={cn(styles.sublinksInnerWrapper, minimized ? styles.minimizedSubLinksInnerWrapper : undefined)}>
            {subLinks?.map((el, i) => {
              return (
                <LeftNavRow subLinks={el.subLinks} href={el.href} icon={el.icon} minimized={minimized} key={i}>
                  {el.children}
                </LeftNavRow>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const getSublinksUrls = (subLinks?: RowProps[]) => {
  if (!subLinks) return [];

  const sublinksUrls: string[] = [];
  const checkSublinks = (subLinks: RowProps[]) => {
    subLinks.forEach((el) => {
      if (el.href && typeof el.href === 'string') sublinksUrls.push(el.href);
      if (el.subLinks) checkSublinks(el.subLinks);
    });
  };
  checkSublinks(subLinks);
  return sublinksUrls;
};
